<template>
  <div class="pt-4">
    <heading-tabs
      class="pb-4 mb-2 pl-3"
      :tabs="tabs"
      :selected="selectedTab"
      @select="onTabChange"
    />
    <initiatives-list-table
      class="initiatives-list"
      :key="listKey"
      :selectable="false"
      :selectable-tasks="false"
      :fetcher="fetchInitiatives"
    >
      <div slot="empty-state" class="empty-state text-secondary" v-no-capture>
        {{ isLoggedInUser ? "you don't" : `${user.firstName || user.email} doesn't` }} have any
        {{ entityName.toLowerCase() }}s
      </div>
    </initiatives-list-table>
  </div>
</template>

<script>
import InitiativesListTable from '@/components/initiative/InitiativesListTable'
import HeadingTabs from '@/components/user-page/HeadingTabs'
import isUserNonPrimaryOwner from '@/components/user-page/utils/isUserNonPrimaryOwner'
import isUserPrimaryOwner from '@/components/user-page/utils/isUserPrimaryOwner'
import { ENTITY_MAP, ENTITY_TYPE } from '@/constants/entities'
import * as R from 'ramda'

const TABS = {
  OWNED: 'owned',
  CO_OWNED: 'co-owned'
}

/**
 * This component shows the list of initiatives that are assigned to user
 *
 * Props:
 *  - fetcher
 *    => This prop needs to be a function that returns a promise which resolves to give list of initiatives
 */
export default {
  name: 'UserInitiatives',
  props: {
    fetcher: Function,
    userId: Number,
    entityType: {
      default: ENTITY_TYPE.INITIATIVE,
      type: String
    }
  },
  components: {
    HeadingTabs,
    InitiativesListTable
  },
  data() {
    return {
      selectedTab: null,
      listKey: 1
    }
  },
  computed: {
    entityName() {
      return ENTITY_MAP[this.entityType].name
    },
    tabs() {
      return [
        {
          id: TABS.OWNED,
          label: this.isLoggedInUser
            ? `All ${this.entityName}s you own`
            : `All ${this.entityName}s ${this.user.firstName} owns`
        },
        { id: TABS.CO_OWNED, label: this.isLoggedInUser ? 'co-own' : 'co-owns' }
      ]
    },
    user() {
      return this.$store.getters.userById(this.ownerId) || {}
    },
    ownerId() {
      const userId = this.userId || this.$store.getters.user.id
      return Number(userId)
    },
    isLoggedInUser() {
      const loggedInUser = this.$store.getters.user
      return this.ownerId === loggedInUser.id
    }
  },
  methods: {
    onTabChange(selectedTab) {
      this.selectedTab = selectedTab
    },
    isUserPrimaryOwner(ownerships) {
      return isUserPrimaryOwner(ownerships, this.ownerId)
    },
    isUserNonPrimaryOwner(ownerships) {
      return isUserNonPrimaryOwner(ownerships, this.ownerId)
    },
    async fetchInitiatives(params) {
      const initiatives = await this.fetcher(params)
      const ownedInitiatives = this.getOwnedInitiatives(initiatives)
      const coOwnedInitiatives = this.getCoOwnedInitiatives(initiatives)
      if (!this.selectedTab) {
        if (ownedInitiatives.length === 0 && coOwnedInitiatives.length > 0) {
          this.selectedTab = TABS.CO_OWNED
        } else {
          this.selectedTab = TABS.OWNED
        }
      }
      return this.selectedTab === TABS.OWNED ? ownedInitiatives : coOwnedInitiatives
    },
    getOwnedInitiatives(initiatives) {
      if (initiatives) {
        return initiatives.filter(initiative => this.isUserPrimaryOwner(initiative.ownerships))
      }
      return []
    },
    getCoOwnedInitiatives(initiatives) {
      if (initiatives) {
        const coOwnedGoals = initiatives.filter(initiative =>
          this.isUserNonPrimaryOwner(initiative.ownerships)
        )
        return R.differenceWith(
          (a, b) => a.id === b.id,
          coOwnedGoals,
          this.getOwnedInitiatives(initiatives)
        )
      }
      return []
    }
  },
  watch: {
    selectedTab(ignore, oldValue) {
      if (oldValue) {
        this.listKey++
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.initiatives-list {
  margin-left: -25px;
}
.empty-state {
  padding-left: 43px;
}
@include media-breakpoint-down(md) {
  .initiatives-list {
    margin-left: 0;
  }
}
</style>
